import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseApi";
import {
  ApiResponse,
  PaginatedResponse,
  StockTransfer,
  CreateStockTransferData,
  RejectStockTransferData,
  StockTransferFilters,
} from "@/types";

export const stockTransferApi = createApi({
  reducerPath: "stockTransferApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["StockTransfer"],
  endpoints: builder => ({
    // Get all stock transfers with filtering
    getStockTransfers: builder.query<
      ApiResponse<PaginatedResponse<StockTransfer>>,
      StockTransferFilters | void
    >({
      query: (params = {}) => {
        if (typeof params === "undefined") {
          return "/stock-transfers";
        }

        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, value.toString());
          }
        });
        return `/stock-transfers?${searchParams.toString()}`;
      },
      providesTags: result =>
        result?.data?.data
          ? [
              ...result.data.data.map(({ id }) => ({ type: "StockTransfer" as const, id })),
              { type: "StockTransfer", id: "LIST" },
            ]
          : [{ type: "StockTransfer", id: "LIST" }],
    }),

    // Get stock transfer by ID
    getStockTransferById: builder.query<ApiResponse<StockTransfer>, string>({
      query: id => `/stock-transfers/${id}`,
      providesTags: (_result, _error, id) => [{ type: "StockTransfer", id }],
    }),

    // Create stock transfer request
    createStockTransfer: builder.mutation<ApiResponse<StockTransfer>, CreateStockTransferData>({
      query: data => ({
        url: "/stock-transfers",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "StockTransfer", id: "LIST" }],
    }),

    // Approve stock transfer
    approveStockTransfer: builder.mutation<ApiResponse<StockTransfer>, string>({
      query: id => ({
        url: `/stock-transfers/${id}/approve`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockTransfer", id },
        { type: "StockTransfer", id: "LIST" },
      ],
    }),

    // Reject stock transfer
    rejectStockTransfer: builder.mutation<
      ApiResponse<StockTransfer>,
      { id: string; data: RejectStockTransferData }
    >({
      query: ({ id, data }) => ({
        url: `/stock-transfers/${id}/reject`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "StockTransfer", id },
        { type: "StockTransfer", id: "LIST" },
      ],
    }),

    // Cancel stock transfer
    cancelStockTransfer: builder.mutation<ApiResponse<StockTransfer>, string>({
      query: id => ({
        url: `/stock-transfers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "StockTransfer", id },
        { type: "StockTransfer", id: "LIST" },
      ],
    }),

    // Get pending transfers for outlet
    getPendingTransfers: builder.query<ApiResponse<StockTransfer[]>, string>({
      query: outletId => `/stock-transfers/pending/${outletId}`,
      providesTags: (_result, _error, outletId) => [
        { type: "StockTransfer", id: `PENDING_${outletId}` },
      ],
    }),

    // Get transfer history for outlet
    getTransferHistory: builder.query<
      ApiResponse<StockTransfer[]>,
      { outletId: string; filters?: Partial<StockTransferFilters> }
    >({
      query: ({ outletId, filters = {} }) => {
        const searchParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, value.toString());
          }
        });
        return `/stock-transfers/history/${outletId}?${searchParams.toString()}`;
      },
      providesTags: (_result, _error, { outletId }) => [
        { type: "StockTransfer", id: `HISTORY_${outletId}` },
      ],
    }),
  }),
});

export const {
  useGetStockTransfersQuery,
  useGetStockTransferByIdQuery,
  useCreateStockTransferMutation,
  useApproveStockTransferMutation,
  useRejectStockTransferMutation,
  useCancelStockTransferMutation,
  useGetPendingTransfersQuery,
  useGetTransferHistoryQuery,
} = stockTransferApi;
